import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeDto } from "@qa/shared";
import { api, ApiError } from "../api/client";

interface AuthValue {
  me: MeDto | null;
  loading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}
const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<MeDto>("/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const me = q.data ?? null;
  const value: AuthValue = {
    me,
    loading: q.isLoading,
    isAdmin: !!me?.azioni.includes("admin"),
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    logout: async () => {
      await api.post("/auth/logout");
      // me a null subito (Protected porta al login); il resto della cache via, così il prossimo utente non vede dati altrui
      qc.setQueryData(["me"], null);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth fuori da AuthProvider");
  return v;
}
